<template>
  <b-container ref="container" class="position-relative" :class="fullscreen ? 'fullContent' : ''" fluid>
    <div class="full-mask" v-if="!complete && mfsAdmin && !startedByAdmin && !adminEditing"></div>
    <b-row class="pb-lg-0 pb-md-8 pb-0">
      <b-col class="px-3 pb-5 px-6-md pb-2-md shadow bg-white" offset-xl="2" xl="8">
        <div class="d-flex justify-content-between py-3 above-full-mask flex-wrap" :class="isExtraSmall ? 'flex-dir-col' : ''">
          <div>
            <h2>Landowner Report Summary</h2>
            <router-link tag="a" :to="`/notification/${notification.id}`">
              Notification #{{notification.fonNumber}}
            </router-link>
            <b-alert
              v-if="landownerReport.editedByUserName"
              variant="info"
              show
              class="mt-2 mb-0"
            >
              <div class="d-flex justify-content-between align-items-center">
                <span>
                  <font-awesome-icon icon="pencil-alt" class="mr-1" />
                  Edited by <strong>{{ landownerReport.editedByUserName }}</strong>
                  <span v-if="landownerReport.editedAt">
                    on {{ landownerReport.editedAt | dateString }}
                  </span>
                </span>
                <a
                  href="#"
                  class="hide-on-print show-original-link ml-3"
                  @click.prevent="openOriginalReport"
                >
                  Show Original Report
                </a>
              </div>
            </b-alert>
          </div>
          <div v-if="landownerReport.statusId === 2 || landownerReport.statusId === 4 || landownerReport.statusId === 5" class="d-flex justify-content-end ml-auto pl-3 summary-buttons" :class="isWideDesktop ? 'flex-row align-items-center' : 'flex-column align-items-stretch'">
            <b-button
              class="hide-on-print my-1 mr-2"
              @click="printSummary" pill>
              {{ printButtonText }}
            </b-button>
            <b-button
              v-if="mfsAdmin && !adminEditing"
              class="hide-on-print my-1 mr-2"
              variant="warning"
              @click="$refs.adminEditConfirmModal.show()"
              pill>
              {{ editButtonText }}
            </b-button>
          </div>
        </div>
        <div v-if="anyErrors && !errorsLoading">
          <b-alert v-if="errors.generalDetails.length" variant="danger" show>
            Please return to the General Details page and fix the following errors:
            <ul class="mb-0">
              <li v-for="error in errors.generalDetails" :key="error">
                {{error}}
              </li>
            </ul>
          </b-alert>
          <b-alert v-if="errors.involvement.length" variant="danger" show>
            Please return to the Involvement page and fix the following errors:
            <ul class="mb-0">
              <li v-for="error in errors.involvement" :key="error">
                {{error}}
              </li>
            </ul>
          </b-alert>
          <b-alert v-if="errors.activities.length" variant="danger" show>
            Please return to the Activities page and fix the following errors:
            <ul class="mb-0">
              <li v-for="error in errors.activities" :key="error">
                {{error}}
              </li>
            </ul>
          </b-alert>
          <b-alert v-if="errors.stumpage.length" variant="danger" show>
            Please return to the Stumpage Information page and fix the following errors:
            <ul class="mb-0">
              <li v-for="error in errors.stumpage" :key="error">
                {{error}}
              </li>
            </ul>
          </b-alert>
        </div>
        <SummaryCard
          id="generalDetailsCard"
          title="General Details"
          :collapsible="false"
          :value="false"
        >
          <GeneralDetails />
        </SummaryCard>
        <SummaryCard
          id="involvementDetailsCard"
          title="Involvement Details"
          :collapsible="false"
          :value="false"
        >
          <InvolvementDetails />
        </SummaryCard>
        <SummaryCard
          id="activityDetailsCard"
          title="Activity Details"
          :collapsible="false"
          :value="false"
        >
          <ActivityDetails />
        </SummaryCard>
        <SummaryCard
          id="stumpageDetailsCard"
          title="Stumpage Details"
          :collapsible="false"
          :value="false"
        >
          <StumpageDetails />
        </SummaryCard>
        <div class="text-right">
          <CertifyModal
            v-if="!complete && !adminEditing"
            class="mb-5"
            :modalDisabled="anyErrors || errorsLoading || (!complete && mfsAdmin && !startedByAdmin)"
            :isAdminEdit="false"
          />
        </div>
      </b-col>
    </b-row>
    <b-modal
      ref="certifiedModal"
      content-class="px-5"
      id="certified"
      title="Successfully Certified"
      @ok="certifiedOk"
      no-close-on-esc
      no-close-on-backdrop
      footer-class="justify-content-center"
      hide-header-close
      centered
    >
      You have successfully certified and submitted the {{landownerReport.reportingYear}}
      Landowner Report for Notification <strong>{{notification.fonNumber}}</strong>.
      <template v-if="landownerReport.isFinalYear && !landownerReport.harvestComplete">
       <br/>
       <div>REMINDER: if harvest activity continues beyond
        {{notification.operationEndDate | dateString}}, the FON end date, you MUST file a new FON
        for the additional harvesting, because a FON cannot be extended.</div>
      </template>
      <template v-slot:modal-footer="{ ok }">
        <b-button pill @click="ok()" aria-label="Go to Notification Details">
          Ok
        </b-button>
      </template>
    </b-modal>
    <b-modal
      ref="adminEditConfirmModal"
      id="adminEditConfirmModal"
      title="Edit Landowner Report"
      content-class="px-5"
      centered
      hide-header-close
      no-close-on-esc
      no-close-on-backdrop
      footer-class="justify-content-center"
    >
      <p>
        You are about to edit a completed Landowner Report. The original report
        will be archived for reference. Any changes you make will be saved and
        visible to the landowner.
      </p>
      <p class="mb-0">
        Please add a comment in each section you modify to explain the reason
        for the change.
      </p>
      <template v-slot:modal-footer>
        <b-button pill variant="danger" @click="$refs.adminEditConfirmModal.hide()">
          Cancel
        </b-button>
        <b-button pill @click="confirmAdminEdit">
          Continue
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Vue from 'vue';
import ActivityDetails from '@/components/landownerreport/summary/ActivityDetails.vue';
import CertifyModal from '@/components/landownerreport/summary/CertifyModal.vue';
import GeneralDetails from '@/components/landownerreport/summary/GeneralDetails.vue';
import InvolvementDetails from '@/components/landownerreport/summary/InvolvementDetails.vue';
import StumpageDetails from '@/components/landownerreport/summary/StumpageDetails.vue';
import SummaryCard from '@/components/common/SummaryCard.vue';
import Utilities from '@/mixins/Utilities';

export default {
  name: 'Summary',
  mixins: [Utilities],
  components: {
    ActivityDetails,
    CertifyModal,
    GeneralDetails,
    InvolvementDetails,
    StumpageDetails,
    SummaryCard,
  },
  props: {
    fullscreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    ...mapGetters('User', ['mfsAdmin']),
    ...mapState('LandownerReport', {
      landownerReport: state => state.landownerReport,
      errors: state => state.errors,
      errorsLoading: state => state.errorsLoading,
      showSubmittedModal: state => state.showSubmittedModal,
    }),
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
    ...mapState(['isExtraSmall']),
    ...mapGetters('LandownerReport', ['anyErrors', 'started', 'complete', 'adminEditing']),
    isNarrowDesktop() {
      return this.windowWidth >= 1200 && this.windowWidth <= 1350;
    },
    isWideDesktop() {
      return this.windowWidth > 1750;
    },
    printButtonText() {
      return this.isNarrowDesktop ? 'Print this Report' : 'Print this Landowner Report';
    },
    editButtonText() {
      return this.isNarrowDesktop ? 'Edit this Report' : 'Edit this Landowner Report';
    },
    certifiedMessage() {
      return `You have successfully certified and submitted the
        ${this.landownerReport.reportingYear} Landowner Report for Notification
        ${this.notification.fonNumber}.`;
    },
    startedByAdmin() {
      return this.landownerReport.statusId === 3;
    },
  },
  created() {
    this.reload();
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    if (!this.complete && this.mfsAdmin && !this.startedByAdmin && !this.adminEditing) {
      if (this.disableTabbing) {
        setTimeout(() => {
          this.disableTabbing(this.$refs.container);
        }, 500);
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  watch: {
    showSubmittedModal(value) {
      if (value) this.showModal();
      else this.hideModal();
    },
  },
  methods: {
    onWindowResize() {
      this.windowWidth = window.innerWidth;
    },
    certifiedOk() {
      this.$store.dispatch('LandownerReport/hideSubmittedModal');
      this.$router.push({ name: 'notification', params: { id: this.notification.id } });
    },
    hideModal() {
      this.$refs.certifiedModal.hide();
    },
    showModal() {
      this.$refs.certifiedModal.show();
    },
    reload() {
      if (this.adminEditing) {
        this.$store.dispatch('LandownerReport/adminValidate');
      } else {
        this.$store.dispatch('LandownerReport/validate');
      }
    },
    async submitWrapper() {
      return true;
    },
    printSummary() {
      this.showPosting = false;
      Vue.nextTick(() => window.print());
    },
    startAdminEdit() {
      this.$store.dispatch('LandownerReport/startAdminEdit');
    },
    confirmAdminEdit() {
      this.$refs.adminEditConfirmModal.hide();
      this.startAdminEdit();
    },
    openOriginalReport() {
      const route = this.$router.resolve({
        name: 'originalLandownerReport',
        params: { id: this.landownerReport.id },
      });
      window.open(route.href, '_blank');
    },
  },
};
</script>
<style>
.flex-dir-col{
  flex-direction: column !important;
}
.flex-dir-col .summary-buttons {
  align-self: flex-end;
}
.summary-buttons {
  gap: 0.25rem;
}
.show-original-link {
  white-space: nowrap;
  font-weight: 600;
  text-decoration: underline !important;
}

</style>
